import React from 'react';
import { AlertTriangle, Info, X, XCircle } from 'lucide-react';

/**
 * Banner — a page-level notice (F10). Use it for persistent account or setup state
 * (BAA grace period, setup incomplete, EVV incomplete); use Toast for transient feedback.
 *
 *   <Banner variant="warning" title="Setup incomplete" action={<Link to="/admin">Finish setup</Link>}>
 *     Medicaid claims are blocked until EVV setup is complete.
 *   </Banner>
 */
export type BannerVariant = 'info' | 'warning' | 'error' | 'neutral';

export interface BannerProps {
  variant?: BannerVariant;
  title?: string;
  children?: React.ReactNode;
  /** Optional call to action rendered on the right (a link or button). */
  action?: React.ReactNode;
  /** When set, a dismiss button is shown. */
  onDismiss?: () => void;
  className?: string;
  'data-testid'?: string;
}

const VARIANT_STYLES: Record<BannerVariant, string> = {
  info: 'bg-sky-50 border-sky-300 text-sky-900',
  warning: 'bg-amber-50 border-amber-300 text-amber-900',
  error: 'bg-red-50 border-red-300 text-red-900',
  neutral: 'bg-slate-100 border-slate-300 text-slate-800',
};

const VARIANT_ICON: Record<BannerVariant, React.ReactNode> = {
  info: <Info size={18} aria-hidden="true" />,
  warning: <AlertTriangle size={18} aria-hidden="true" />,
  error: <XCircle size={18} aria-hidden="true" />,
  neutral: <Info size={18} aria-hidden="true" />,
};

const Banner: React.FC<BannerProps> = ({
  variant = 'info',
  title,
  children,
  action,
  onDismiss,
  className = '',
  'data-testid': testId,
}) => (
  <div
    role={variant === 'error' ? 'alert' : 'status'}
    data-testid={testId}
    className={`flex items-start gap-3 rounded-md border px-4 py-3 text-sm ${VARIANT_STYLES[variant]} ${className}`}
  >
    <span className="mt-0.5 flex-shrink-0">{VARIANT_ICON[variant]}</span>
    <div className="flex-1 text-left">
      {title && <p className="font-semibold">{title}</p>}
      {children && <div className={title ? 'mt-0.5' : ''}>{children}</div>}
    </div>
    {action && <div className="flex-shrink-0 self-center">{action}</div>}
    {onDismiss && (
      <button
        type="button"
        onClick={onDismiss}
        className="flex-shrink-0 rounded p-0.5 opacity-70 hover:opacity-100"
        aria-label="Dismiss"
      >
        <X size={16} aria-hidden="true" />
      </button>
    )}
  </div>
);

export default Banner;
